if (!customElements.get('muzdan-facets-drawer')) {
  class MuzdanFacetsDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.rootDrawer = this.closest('.drawer__container');

      /*
       * Use delegated listeners so the animation still works after
       * Shopify replaces facet contents following an AJAX update.
       */
      this.addEventListener(
        'toggle',
        (event) => {
          const section = event.target.closest(
            '.facets__dior-section'
          );

          if (
            !section ||
            !section.open ||
            section.classList.contains('is-returning')
          ) {
            return;
          }

          this.getSections().forEach((otherSection) => {
            if (
              otherSection !== section &&
              otherSection.open
            ) {
              otherSection.classList.remove(
                'is-entering',
                'is-returning'
              );
              otherSection.removeAttribute('open');
            }
          });

          this.startPanelAnimation(section);
        },
        true
      );

      this.addEventListener('click', (event) => {
        const backButton = event.target.closest(
          '[data-facets-panel-back]'
        );

        if (backButton) {
          event.preventDefault();
          event.stopPropagation();

          const section = backButton.closest(
            '.facets__dior-section'
          );
          const summary = section?.querySelector(
            ':scope > .facets__dior-summary'
          );

          this.closePanel(section, summary);
          return;
        }

        /*
         * This is a fallback for browsers that do not propagate the
         * details toggle event through the custom element.
         */
        const summary = event.target.closest(
          '.facets__dior-summary'
        );

        if (!summary) return;

        const section = summary.closest(
          '.facets__dior-section'
        );

        requestAnimationFrame(() => {
          if (
            section?.open &&
            !section.classList.contains('is-entering')
          ) {
            this.startPanelAnimation(section);
          }
        });
      });

      this.addEventListener('change', (event) => {
        const activeSection = event.target.closest(
          '.facets__dior-section'
        );

        if (activeSection && !activeSection.open) {
          activeSection.setAttribute('open', '');
        }

        const sortInput = event.target.closest(
          '.facets__dior-sort-option input[name="sort_by"]'
        );

        if (!sortInput) return;

        const sortSection = sortInput.closest(
          '.facets__dior-sort'
        );
        const sortValue = sortSection?.querySelector(
          ':scope > .facets__dior-summary .facets__dior-summary-value'
        );
        const label = sortInput.closest(
          '.facets__dior-sort-option'
        )?.querySelector('span');

        if (sortValue && label) {
          sortValue.textContent = label.textContent.trim();
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        const openSection = this.querySelector(
          '.facets__dior-section[open]'
        );

        if (!openSection) return;

        event.preventDefault();
        event.stopPropagation();

        const summary = openSection.querySelector(
          ':scope > .facets__dior-summary'
        );

        this.closePanel(openSection, summary);
      });

      if (this.rootDrawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (
            this.rootDrawer.hasAttribute('open') ||
            this.rootDrawer.classList.contains('menu-opening')
          ) {
            return;
          }

          this.getSections().forEach((section) => {
            section.classList.remove(
              'is-entering',
              'is-returning'
            );
            section.removeAttribute('open');
          });
        });

        this.drawerObserver.observe(this.rootDrawer, {
          attributes: true,
          attributeFilter: ['class', 'open']
        });
      }
    }

    getSections() {
      return Array.from(
        this.querySelectorAll('.facets__dior-section')
      );
    }

    startPanelAnimation(section) {
      const panel = section.querySelector(
        ':scope > .facets__dior-panel'
      );

      if (!panel) return;

      /*
       * A parent list may already be scrolled when a child is
       * opened. Reset both scroll regions so the child header
       * always enters at the top of the drawer.
       */
      const menu = section.closest('.facets__dior-menu');
      const panelBody = panel.querySelector(
        '.facets__dior-panel-body'
      );

      if (menu) menu.scrollTop = 0;
      if (panelBody) panelBody.scrollTop = 0;

      section.classList.remove(
        'is-entering',
        'is-returning'
      );

      /*
       * Force a layout read between removing and adding the class.
       * This restarts the transition on every visit.
       */
      void panel.offsetWidth;
      section.classList.add('is-entering');

      panel.addEventListener(
        'animationend',
        () => {
          section.classList.remove('is-entering');
        },
        { once: true }
      );
    }

    closePanel(section, summary) {
      if (
        !section ||
        section.classList.contains('is-returning')
      ) {
        return;
      }

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      section.classList.remove('is-entering');

      if (reducedMotion) {
        section.removeAttribute('open');
        summary?.focus();
        return;
      }

      section.classList.add('is-returning');

      window.setTimeout(() => {
        section.removeAttribute('open');
        section.classList.remove('is-returning');
        summary?.focus();
      }, 420);
    }

    disconnectedCallback() {
      this.drawerObserver?.disconnect();
    }
  }

  customElements.define(
    'muzdan-facets-drawer',
    MuzdanFacetsDrawer
  );
}